import { SelectOption } from '../../../framework/valueobject/select-option.ts';
import React, { ReactNode, useMemo } from 'react';
import { createListCollection, Portal, Select } from '@chakra-ui/react';

interface SelectInputProps<T> {
  ariaLabel: string;
  placeholder?: string;
  options: SelectOption<T>[];
  selectedValue: T | null;
  onChange: (value: T | null) => void;
  isResettable?: boolean;
  parentRef?: React.RefObject<HTMLDivElement | null>;
  dataTest?: string;
  disabled?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

/**
 * Renders a custom select input component with options, placeholder, and additional configurations.
 */
export default function SelectInput<T>({
  ariaLabel,
  placeholder,
  options,
  selectedValue,
  onChange,
  isResettable = false,
  parentRef,
  dataTest,
  disabled = false,
  leftIcon,
  rightIcon,
}: SelectInputProps<T>) {
  // create collection from option
  const collection = useMemo(() => {
    // We use the label here because Chakra UI can only handle strings with the selectValues.
    // We therefore save the label as a value and select it again from our option list during the onChange.
    return createListCollection({
      items: options.map((option) => {
        return {
          label: option.label,
          value: option.label,
        };
      }),
    });
  }, [options]);

  // create selected values for Chakra
  const selectedValuesForChakra: string[] = useMemo(() => {
    const selectedLabel = options.find((option) => option.value == selectedValue)?.label;
    if (selectedLabel) {
      return [selectedLabel];
    }
    return [];
  }, [options, selectedValue]);

  return (
    <Select.Root
      data-test={dataTest}
      value={selectedValuesForChakra}
      collection={collection}
      disabled={disabled}
      size={'xs'}
      variant={'subtle'}
      onValueChange={(e) => {
        // there is only one selected at the same time
        onChange(options.find((option) => option.label === e.value[0])?.value ?? null);
      }}
    >
      <Select.Control>
        <Select.Trigger aria-label={ariaLabel}>
          {leftIcon}
          <Select.ValueText placeholder={placeholder} />
          <Select.Indicator>{rightIcon}</Select.Indicator>
        </Select.Trigger>
        {isResettable && (
          <Select.IndicatorGroup>
            <Select.ClearTrigger />
          </Select.IndicatorGroup>
        )}
      </Select.Control>
      {/*To ensure that the selection is positioned correctly, if a dialog is used, you must specify which element is
        the container, otherwise the selection will be rendered behind the dialog*/}
      <Portal container={parentRef}>
        <Select.Positioner>
          <Select.Content>
            {collection.items.map((option) => (
              <Select.Item aria-label={option.label} item={option} key={option.label} cursor={'pointer'}>
                {option.label}
                <Select.ItemIndicator />
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
  );
}
